/**
 * View LaTeX on a phone (Olivia, 10 Oct): the document's source, read-only,
 * without the `% block:<id>` markers the assembler writes before each block and
 * the `\label{blk-<id>}` anchor it puts at each block's start: both are for
 * the editor, and a reader can't do anything with them.
 * The markers still say where each block's lines are, so a selected block's
 * lines can be highlighted.
 */

export interface LatexSource {
  /** The lines as shown: markers removed. */
  lines: string[];
  /** Block id → its first and last shown line (1-based, inclusive). */
  ranges: Map<string, { start: number; end: number }>;
}

const MARKER = /^%\s*block:([0-9a-fA-F-]{8,})\s*$/;
const ANCHOR = /^\\label\{blk-[0-9a-fA-F-]{8,}\}\s*$/;

export function readLatexSource(raw: string): LatexSource {
  const lines: string[] = [];
  const ranges = new Map<string, { start: number; end: number }>();
  let open: { id: string; start: number } | null = null;
  const close = () => {
    if (!open) return;
    // A block ends before the blank lines that separate it from what follows.
    let end = lines.length;
    while (end > open.start && !lines[end - 1].trim()) end--;
    if (end >= open.start) ranges.set(open.id, { start: open.start, end });
    open = null;
  };
  for (const line of raw.split("\n")) {
    const m = MARKER.exec(line.trim());
    if (m) {
      close();
      open = { id: m[1], start: lines.length + 1 };
      continue;
    }
    if (ANCHOR.test(line.trim())) continue;
    // The body ends a block too: nothing after \end{document} belongs to it.
    if (open && /^\\end\{document\}/.test(line.trim())) close();
    lines.push(line);
  }
  close();
  return { lines, ranges };
}
